import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Tabs,
    Tab,
    useTheme,
    Switch,
    FormControlLabel,
    Paper,
    Skeleton,
    Grid,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Snackbar,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getClientById,
    updateClient,
    getClientRoles,
    updateRolePermissions,
    createClientRole,
    getClientDepartments,
    createClientDepartment,
    updateClientDepartment,
    getClientUsers,
    createClientUser,
    updateClientUser,
    getClientSettings,
    patchClientSettings,
    getClientSmsConfig,
    putClientSmsConfig,
    getClientWhatsAppConfig,
    putClientWhatsAppConfig,
    listClients,
    ClientDetail,
    ClientRoleItem,
    PermissionItem,
    ClientDepartmentItem,
    ClientUserItem,
    ClientListItem,
    SmsConfigAdmin,
    WhatsAppConfigAdmin,
    getClientCertificate,
    issueClientCertificate,
    revokeClientCertificate,
    ClientCertificate,
} from '../../services/clients-service';
import { formatFriendlyDate } from '../../utils/dateUtils';
import {
    validatePasswordPolicy,
    generateStrongPassword,
    PASSWORD_POLICY_DESCRIPTION,
} from '../../utils/passwordPolicy';

/** Commissioning date tooltip text. */
const COMMISSIONING_DATE_TOOLTIP =
    'Date when the client was formally commissioned or went live. Optional.';

/** Editable Overview form fields (in-memory while in edit mode). */
function getOverviewFormFromClient(c: ClientDetail | null) {
    if (!c) return null;
    const commissioningDate =
        c.commissioningDate ? new Date(c.commissioningDate).toISOString().slice(0, 10) : '';
    return {
        clientName: c.clientName ?? '',
        contactEmail: c.contactEmail ?? '',
        contactPhone: c.contactPhone ?? '',
        clientAddress: c.clientAddress ?? '',
        clientIndustry: c.clientIndustry ?? '',
        packageId: c.packageId != null ? String(c.packageId) : '',
        isActive: c.isActive ?? true,
        commissioningDate,
    };
}

function isOverviewFormDirty(
    client: ClientDetail | null,
    form: NonNullable<ReturnType<typeof getOverviewFormFromClient>>
): boolean {
    if (!client) return false;
    const clientCommissioning =
        client.commissioningDate ? new Date(client.commissioningDate).toISOString().slice(0, 10) : '';
    return (
        (client.clientName ?? '') !== form.clientName ||
        (client.contactEmail ?? '') !== form.contactEmail ||
        (client.contactPhone ?? '') !== form.contactPhone ||
        (client.clientAddress ?? '') !== form.clientAddress ||
        (client.clientIndustry ?? '') !== form.clientIndustry ||
        (client.packageId != null ? String(client.packageId) : '') !== form.packageId ||
        (client.isActive ?? true) !== form.isActive ||
        clientCommissioning !== form.commissioningDate
    );
}

const TAB_OVERVIEW = 0;
const TAB_USERS = 1;
const TAB_DEPARTMENTS = 2;
const TAB_ROLES = 3;
const TAB_SETTINGS = 4;
const TAB_BADGE = 5;
const TAB_REPORTS = 6;

/** Default SMS form (current client). Used when no config exists and for Cancel reset. */
function emptySmsForm(): Partial<SmsConfigAdmin> & { providerName: string; apiKey: string } {
    return {
        providerName: '',
        apiKey: '',
        templateId: null,
        otpTemplateId: null,
        shortUrlEnabled: false,
        shortUrlExpiryDays: null,
    };
}

/** Default WhatsApp form (current client). Used when no config exists and for Cancel reset. */
function emptyWhatsappForm(): Partial<WhatsAppConfigAdmin> & { providerName: string; apiKey: string } {
    return {
        providerName: '',
        apiKey: '',
        phoneNumberId: null,
        templateId: null,
    };
}

/** Empty SMS config for copy-preview when source client has no config. */
function emptySmsPreview(): SmsConfigAdmin {
    return {
        configId: 0,
        clientId: 0,
        providerName: '',
        apiKey: '',
        apiSecret: null,
        senderId: null,
        templateId: null,
        otpTemplateId: null,
        shortUrlEnabled: false,
        shortUrlProvider: null,
        shortUrlApiKey: null,
        shortUrlExpiryDays: null,
        realTimeResponse: true,
    };
}

/** Empty WhatsApp config for copy-preview when source client has no config. */
function emptyWhatsappPreview(): WhatsAppConfigAdmin {
    return {
        configId: 0,
        clientId: 0,
        providerName: '',
        apiKey: '',
        apiSecret: null,
        phoneNumberId: null,
        businessAccountId: null,
        templateId: null,
    };
}

const ClientDetailPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { clientId } = useParams<{ clientId: string }>();
    const [client, setClient] = useState<ClientDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
    const [togglingActive, setTogglingActive] = useState(false);
    const [overviewEditMode, setOverviewEditMode] = useState(false);
    const [overviewForm, setOverviewForm] = useState<NonNullable<ReturnType<typeof getOverviewFormFromClient>> | null>(null);
    const [overviewSaving, setOverviewSaving] = useState(false);
    const [tabSwitchConfirmOpen, setTabSwitchConfirmOpen] = useState(false);
    const [pendingTab, setPendingTab] = useState<number | null>(null);
    const [tabSwitchConfirmMessage, setTabSwitchConfirmMessage] = useState('You have unsaved changes. Discard and switch tab?');

    // Roles tab
    const [rolesData, setRolesData] = useState<{
        roles: ClientRoleItem[];
        allPermissions: PermissionItem[];
        isSuperAdminClient?: boolean;
    } | null>(null);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
    const [editedPermissionIds, setEditedPermissionIds] = useState<number[]>([]);
    const [rolesSaving, setRolesSaving] = useState(false);
    const [addingRole, setAddingRole] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [newRoleDescription, setNewRoleDescription] = useState('');
    const [newRolePermissionIds, setNewRolePermissionIds] = useState<number[]>([]);
    const [rolesCreateSaving, setRolesCreateSaving] = useState(false);

    // Departments tab
    const [departmentsData, setDepartmentsData] = useState<ClientDepartmentItem[] | null>(null);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [addingDepartment, setAddingDepartment] = useState(false);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [newDepartmentDescription, setNewDepartmentDescription] = useState('');
    const [departmentsCreateSaving, setDepartmentsCreateSaving] = useState(false);
    const [editingDepartmentId, setEditingDepartmentId] = useState<number | null>(null);
    const [editDepartmentName, setEditDepartmentName] = useState('');
    const [editDepartmentDescription, setEditDepartmentDescription] = useState('');
    const [departmentsSaving, setDepartmentsSaving] = useState(false);

    // Users tab
    const [usersData, setUsersData] = useState<ClientUserItem[] | null>(null);
    const [usersLoading, setUsersLoading] = useState(false);
    const [addingUser, setAddingUser] = useState(false);
    const [newUserUsername, setNewUserUsername] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserRoleIds, setNewUserRoleIds] = useState<number[]>([]);
    const [showNewUserPassword, setShowNewUserPassword] = useState(false);
    const [showEditUserNewPassword, setShowEditUserNewPassword] = useState(false);
    const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
    const [usersCreateSaving, setUsersCreateSaving] = useState(false);
    const [editingUserId, setEditingUserId] = useState<number | null>(null);
    const [editUserRoleIds, setEditUserRoleIds] = useState<number[]>([]);
    const [editUserActive, setEditUserActive] = useState(true);
    const [editUserNewPassword, setEditUserNewPassword] = useState('');
    const [usersSaving, setUsersSaving] = useState(false);

    // Settings tab
    const [settingsData, setSettingsData] = useState<Record<string, string> | null>(null);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsForm, setSettingsForm] = useState<Record<string, string>>({});
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [smsConfig, setSmsConfig] = useState<SmsConfigAdmin | null>(null);
    const [smsConfigLoading, setSmsConfigLoading] = useState(false);
    const [smsForm, setSmsForm] = useState<Partial<SmsConfigAdmin> & { providerName: string; apiKey: string }>(() => emptySmsForm());
    const [smsSaving, setSmsSaving] = useState(false);
    const [copySmsLoading, setCopySmsLoading] = useState(false);
    const [whatsappConfig, setWhatsappConfig] = useState<WhatsAppConfigAdmin | null>(null);
    const [whatsappConfigLoading, setWhatsappConfigLoading] = useState(false);
    const [whatsappForm, setWhatsappForm] = useState<Partial<WhatsAppConfigAdmin> & { providerName: string; apiKey: string }>(() => emptyWhatsappForm());
    const [whatsappSaving, setWhatsappSaving] = useState(false);
    const [copyWhatsappLoading, setCopyWhatsappLoading] = useState(false);
    const [clientsList, setClientsList] = useState<ClientListItem[]>([]);
    // Copy-from-client preview (show source client's config, editable, then Apply)
    const [copySourceClientIdSms, setCopySourceClientIdSms] = useState<string>('');
    const [copySourceSmsPreview, setCopySourceSmsPreview] = useState<SmsConfigAdmin | null>(null);
    const [copySourceSmsPreviewLoading, setCopySourceSmsPreviewLoading] = useState(false);
    const [copySourceClientIdWhatsapp, setCopySourceClientIdWhatsapp] = useState<string>('');
    const [copySourceWhatsappPreview, setCopySourceWhatsappPreview] = useState<WhatsAppConfigAdmin | null>(null);
    const [copySourceWhatsappPreviewLoading, setCopySourceWhatsappPreviewLoading] = useState(false);
    const [certificate, setCertificate] = useState<ClientCertificate | null>(null);
    const [certificateLoading, setCertificateLoading] = useState(false);
    const [certificateIssuing, setCertificateIssuing] = useState(false);
    const [certificateRevoking, setCertificateRevoking] = useState(false);
    const [certificateError, setCertificateError] = useState<string | null>(null);
    const [badgeOrgName, setBadgeOrgName] = useState('');
    const [badgeValidUntil, setBadgeValidUntil] = useState('');
    const [badgeDpPortalUrl, setBadgeDpPortalUrl] = useState('');
    const [embedCopiedSnackbar, setEmbedCopiedSnackbar] = useState(false);

    const overviewDirty = overviewForm !== null && client !== null && isOverviewFormDirty(client, overviewForm);
    const hasUnsavedOverview = overviewEditMode && overviewDirty;

    const rolesDirty =
        editingRoleId !== null &&
        rolesData &&
        (() => {
            const role = rolesData.roles.find((r) => r.roleId === editingRoleId);
            if (!role) return false;
            const a = [...role.permissionIds].sort((x, y) => x - y);
            const b = [...editedPermissionIds].sort((x, y) => x - y);
            return a.length !== b.length || a.some((v, i) => v !== b[i]);
        })();
    const addRoleDirty = addingRole && (newRoleName.trim() !== '' || newRolePermissionIds.length > 0);
    const hasUnsavedRoles = (editingRoleId !== null && rolesDirty) || addRoleDirty;

    const addDepartmentDirty = addingDepartment && (newDepartmentName.trim() !== '' || newDepartmentDescription.trim() !== '');
    const departmentEditDirty =
        editingDepartmentId !== null &&
        departmentsData &&
        (() => {
            const dept = departmentsData.find((d) => d.departmentId === editingDepartmentId);
            if (!dept) return false;
            return editDepartmentName !== dept.departmentName || (editDepartmentDescription ?? '') !== (dept.description ?? '');
        })();
    const hasUnsavedDepartments = addDepartmentDirty || (editingDepartmentId !== null && departmentEditDirty);

    const addUserDirty = addingUser && (newUserUsername.trim() !== '' || newUserEmail.trim() !== '' || newUserPassword !== '' || newUserRoleIds.length > 0);
    const userEditDirty =
        editingUserId !== null &&
        usersData &&
        (() => {
            const user = usersData.find((u) => u.userId === editingUserId);
            if (!user) return false;
            const roleMatch = [...user.roleIds].sort((a, b) => a - b).join() === [...editUserRoleIds].sort((a, b) => a - b).join();
            const activeMatch = editUserActive === user.isActive;
            return !roleMatch || !activeMatch || editUserNewPassword.trim() !== '';
        })();
    const hasUnsavedUsers = addUserDirty || (editingUserId !== null && userEditDirty);

    const settingsFormDirty =
        settingsData !== null &&
        (settingsForm.WITHDRAWAL_SLA_DAYS !== settingsData.WITHDRAWAL_SLA_DAYS ||
            settingsForm.PHONE_NOTIFICATION_ENABLE_SMS !== settingsData.PHONE_NOTIFICATION_ENABLE_SMS ||
            settingsForm.PHONE_NOTIFICATION_ENABLE_WHATSAPP !== settingsData.PHONE_NOTIFICATION_ENABLE_WHATSAPP ||
            settingsForm.PHONE_NOTIFICATION_PREFERRED_METHOD !== settingsData.PHONE_NOTIFICATION_PREFERRED_METHOD);
    const smsFormDirty =
        smsConfig !== null
            ? (smsForm.providerName !== smsConfig.providerName ||
                  smsForm.apiKey !== smsConfig.apiKey ||
                  (smsForm.templateId ?? '') !== (smsConfig.templateId ?? '') ||
                  (smsForm.otpTemplateId ?? '') !== (smsConfig.otpTemplateId ?? '') ||
                  (smsForm.shortUrlEnabled ?? false) !== smsConfig.shortUrlEnabled ||
                  (smsForm.shortUrlExpiryDays ?? null) !== smsConfig.shortUrlExpiryDays)
            : (smsForm.providerName !== '' || smsForm.apiKey !== '');
    const whatsappFormDirty =
        whatsappConfig !== null
            ? (whatsappForm.providerName !== whatsappConfig.providerName ||
                  whatsappForm.apiKey !== whatsappConfig.apiKey ||
                  (whatsappForm.phoneNumberId ?? '') !== (whatsappConfig.phoneNumberId ?? '') ||
                  (whatsappForm.templateId ?? '') !== (whatsappConfig.templateId ?? ''))
            : (whatsappForm.providerName !== '' || whatsappForm.apiKey !== '');
    const hasUnsavedSettings =
        settingsFormDirty ||
        smsFormDirty ||
        whatsappFormDirty ||
        copySourceSmsPreview !== null ||
        copySourceWhatsappPreview !== null;

    const id = clientId ? parseInt(clientId, 10) : NaN;
    const validId = !isNaN(id) && id >= 1;

    const loadClient = React.useCallback(() => {
        if (!validId) return;
        setLoading(true);
        setError(null);
        getClientById(id)
            .then((data) => {
                setClient(data ?? null);
                if (data == null) setError('Client not found');
            })
            .catch(() => setError('Failed to load client'))
            .finally(() => setLoading(false));
    }, [id, validId]);

    useEffect(() => {
        loadClient();
    }, [loadClient]);

    const openAddFormWhenRolesLoadedRef = React.useRef(false);
    const loadRoles = React.useCallback(() => {
        if (!validId) return;
        setRolesLoading(true);
        getClientRoles(id)
            .then((data) => {
                setRolesData(data ?? null);
                if (data && openAddFormWhenRolesLoadedRef.current) {
                    openAddFormWhenRolesLoadedRef.current = false;
                    setAddingRole(true);
                }
            })
            .finally(() => setRolesLoading(false));
    }, [id, validId]);

    useEffect(() => {
        if (activeTab === TAB_ROLES && validId) loadRoles();
    }, [activeTab, validId, loadRoles]);

    const loadDepartments = React.useCallback(() => {
        if (!validId) return;
        setDepartmentsLoading(true);
        getClientDepartments(id)
            .then((data) => setDepartmentsData(data ?? null))
            .finally(() => setDepartmentsLoading(false));
    }, [id, validId]);

    useEffect(() => {
        if (activeTab === TAB_DEPARTMENTS && validId) loadDepartments();
    }, [activeTab, validId, loadDepartments]);

    const loadUsers = React.useCallback(() => {
        if (!validId) return;
        setUsersLoading(true);
        getClientUsers(id)
            .then((data) => setUsersData(data ?? null))
            .finally(() => setUsersLoading(false));
    }, [id, validId]);

    useEffect(() => {
        if (activeTab === TAB_USERS && validId) {
            loadUsers();
            loadRoles();
        }
    }, [activeTab, validId, loadUsers, loadRoles]);

    const loadSettings = React.useCallback(() => {
        if (!validId) return;
        setSettingsLoading(true);
        getClientSettings(id).then((data) => {
            setSettingsData(data ?? null);
            setSettingsForm(data ?? {});
        }).finally(() => setSettingsLoading(false));
    }, [id, validId]);

    const loadSmsConfig = React.useCallback(() => {
        if (!validId) return;
        setSmsConfigLoading(true);
        getClientSmsConfig(id).then((data) => {
            setSmsConfig(data ?? null);
            setSmsForm(data ? { ...data } : emptySmsForm());
        }).finally(() => setSmsConfigLoading(false));
    }, [id, validId]);

    const loadWhatsappConfig = React.useCallback(() => {
        if (!validId) return;
        setWhatsappConfigLoading(true);
        getClientWhatsAppConfig(id).then((data) => {
            setWhatsappConfig(data ?? null);
            setWhatsappForm(data ? { ...data } : emptyWhatsappForm());
        }).finally(() => setWhatsappConfigLoading(false));
    }, [id, validId]);

    useEffect(() => {
        if (activeTab === TAB_SETTINGS && validId) {
            loadSettings();
            loadSmsConfig();
            loadWhatsappConfig();
            listClients().then((list) => setClientsList(list ?? []));
        }
    }, [activeTab, validId, loadSettings, loadSmsConfig, loadWhatsappConfig]);

    const loadCertificate = React.useCallback(() => {
        if (!validId) return;
        setCertificateLoading(true);
        setCertificateError(null);
        getClientCertificate(id)
            .then((data) => {
                const cert = data?.certificate ?? null;
                setCertificate(cert);
                if (cert) {
                    setBadgeOrgName(cert.organisationName);
                    setBadgeValidUntil(cert.validUntil);
                    setBadgeDpPortalUrl(cert.dpPortalUrl ?? '');
                } else if (client) {
                    // Prefer legal brand name for Lakewood demo typo in ClientName
                    const defaultName =
                        client.subdomain === 'lakewood-realty' ? 'Lakewood Realty' : client.clientName;
                    setBadgeOrgName(defaultName);
                    const nextYear = new Date();
                    nextYear.setFullYear(nextYear.getFullYear() + 1);
                    setBadgeValidUntil(nextYear.toISOString().slice(0, 10));
                    setBadgeDpPortalUrl('');
                }
            })
            .catch(() => setCertificateError('Failed to load certificate'))
            .finally(() => setCertificateLoading(false));
    }, [id, validId, client]);

    useEffect(() => {
        if (activeTab === TAB_BADGE && validId) {
            loadCertificate();
        }
    }, [activeTab, validId, loadCertificate]);

    // When user selects a client in "Copy from client" (SMS), fetch that client's SMS config for preview
    useEffect(() => {
        const srcId = copySourceClientIdSms ? parseInt(copySourceClientIdSms, 10) : 0;
        if (!srcId) {
            setCopySourceSmsPreview(null);
            return;
        }
        setCopySourceSmsPreviewLoading(true);
        getClientSmsConfig(srcId)
            .then((data) => setCopySourceSmsPreview(data ?? emptySmsPreview()))
            .finally(() => setCopySourceSmsPreviewLoading(false));
    }, [copySourceClientIdSms]);

    // When user selects a client in "Copy from client" (WhatsApp), fetch that client's WhatsApp config for preview
    useEffect(() => {
        const srcId = copySourceClientIdWhatsapp ? parseInt(copySourceClientIdWhatsapp, 10) : 0;
        if (!srcId) {
            setCopySourceWhatsappPreview(null);
            return;
        }
        setCopySourceWhatsappPreviewLoading(true);
        getClientWhatsAppConfig(srcId)
            .then((data) => setCopySourceWhatsappPreview(data ?? emptyWhatsappPreview()))
            .finally(() => setCopySourceWhatsappPreviewLoading(false));
    }, [copySourceClientIdWhatsapp]);

    const handleBack = () => navigate('/admin/clients');

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        if (newValue === activeTab) return;
        if (activeTab === TAB_OVERVIEW && hasUnsavedOverview) {
            setPendingTab(newValue);
            setTabSwitchConfirmMessage('You have unsaved changes in Overview. Discard and switch tab?');
            setTabSwitchConfirmOpen(true);
            return;
        }
        if (activeTab === TAB_ROLES && hasUnsavedRoles) {
            setPendingTab(newValue);
            setTabSwitchConfirmMessage('You have unsaved changes in Roles. Discard and switch tab?');
            setTabSwitchConfirmOpen(true);
            return;
        }
        if (activeTab === TAB_DEPARTMENTS && hasUnsavedDepartments) {
            setPendingTab(newValue);
            setTabSwitchConfirmMessage('You have unsaved changes in Departments. Discard and switch tab?');
            setTabSwitchConfirmOpen(true);
            return;
        }
        if (activeTab === TAB_USERS && hasUnsavedUsers) {
            setPendingTab(newValue);
            setTabSwitchConfirmMessage('You have unsaved changes in Users. Discard and switch tab?');
            setTabSwitchConfirmOpen(true);
            return;
        }
        if (activeTab === TAB_SETTINGS && hasUnsavedSettings) {
            setPendingTab(newValue);
            setTabSwitchConfirmMessage('You have unsaved changes in Settings. Discard and switch tab?');
            setTabSwitchConfirmOpen(true);
            return;
        }
        setActiveTab(newValue);
    };

    const handleTabSwitchConfirm = () => {
        setOverviewEditMode(false);
        setOverviewForm(null);
        setEditingRoleId(null);
        setAddingRole(false);
        setNewRoleName('');
        setNewRoleDescription('');
        setNewRolePermissionIds([]);
        setEditingDepartmentId(null);
        setAddingDepartment(false);
        setNewDepartmentName('');
        setNewDepartmentDescription('');
        setEditingUserId(null);
        setAddingUser(false);
        setNewUserUsername('');
        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserRoleIds([]);
        setEditUserNewPassword('');
        setSettingsForm(settingsData ?? {});
        setSmsForm(smsConfig ? { ...smsConfig } : emptySmsForm());
        setWhatsappForm(whatsappConfig ? { ...whatsappConfig } : emptyWhatsappForm());
        setCopySourceClientIdSms('');
        setCopySourceSmsPreview(null);
        setCopySourceClientIdWhatsapp('');
        setCopySourceWhatsappPreview(null);
        if (pendingTab !== null) {
            setActiveTab(pendingTab);
            setPendingTab(null);
        }
        setTabSwitchConfirmOpen(false);
    };

    const handleTabSwitchCancel = () => {
        setPendingTab(null);
        setTabSwitchConfirmOpen(false);
    };

    const startOverviewEdit = () => {
        if (client) {
            setOverviewForm(getOverviewFormFromClient(client));
            setOverviewEditMode(true);
        }
    };

    const cancelOverviewEdit = () => {
        setOverviewEditMode(false);
        setOverviewForm(null);
    };

    const saveOverviewEdit = async () => {
        if (!client || !overviewForm || !overviewDirty) return;
        setOverviewSaving(true);
        try {
            const payload = {
                clientName: overviewForm.clientName.trim() || undefined,
                contactEmail: overviewForm.contactEmail.trim() || null,
                contactPhone: overviewForm.contactPhone.trim() || null,
                clientAddress: overviewForm.clientAddress.trim() || null,
                clientIndustry: overviewForm.clientIndustry.trim() || null,
                packageId: overviewForm.packageId.trim() ? (parseInt(overviewForm.packageId.trim(), 10) || null) : null,
                isActive: overviewForm.isActive,
                commissioningDate: overviewForm.commissioningDate.trim()
                    ? new Date(overviewForm.commissioningDate + 'T12:00:00Z').toISOString()
                    : null,
            };
            const updated = await updateClient(client.clientId, payload);
            if (updated) {
                setClient(updated);
                setOverviewEditMode(false);
                setOverviewForm(null);
            }
        } finally {
            setOverviewSaving(false);
        }
    };

    const startRoleEdit = (role: ClientRoleItem) => {
        setEditingRoleId(role.roleId);
        setEditedPermissionIds([...role.permissionIds]);
    };

    const cancelRoleEdit = () => {
        setEditingRoleId(null);
    };

    const startAddRole = () => {
        setAddingRole(true);
        setNewRoleName('');
        setNewRoleDescription('');
        setNewRolePermissionIds([]);
    };

    const cancelAddRole = () => {
        setAddingRole(false);
        setNewRoleName('');
        setNewRoleDescription('');
        setNewRolePermissionIds([]);
    };

    const saveNewRole = async () => {
        if (!validId || !newRoleName.trim()) return;
        setRolesCreateSaving(true);
        const role = await createClientRole(id, {
            roleName: newRoleName.trim(),
            description: newRoleDescription.trim() || null,
            permissionIds: newRolePermissionIds,
        });
        setRolesCreateSaving(false);
        if (role) {
            cancelAddRole();
            loadRoles();
        }
    };

    const saveRolePermissions = async () => {
        if (!validId || editingRoleId === null) return;
        setRolesSaving(true);
        const ok = await updateRolePermissions(id, editingRoleId, editedPermissionIds);
        setRolesSaving(false);
        if (ok) {
            setEditingRoleId(null);
            loadRoles();
        }
    };

    const startAddDepartment = () => {
        setAddingDepartment(true);
        setNewDepartmentName('');
        setNewDepartmentDescription('');
    };

    const cancelAddDepartment = () => {
        setAddingDepartment(false);
        setNewDepartmentName('');
        setNewDepartmentDescription('');
    };

    const saveNewDepartment = async () => {
        if (!validId || !newDepartmentName.trim()) return;
        setDepartmentsCreateSaving(true);
        const dept = await createClientDepartment(id, {
            departmentName: newDepartmentName.trim(),
            description: newDepartmentDescription.trim() || null,
        });
        setDepartmentsCreateSaving(false);
        if (dept) {
            cancelAddDepartment();
            loadDepartments();
        }
    };

    const startDepartmentEdit = (dept: ClientDepartmentItem) => {
        setEditingDepartmentId(dept.departmentId);
        setEditDepartmentName(dept.departmentName);
        setEditDepartmentDescription(dept.description ?? '');
    };

    const cancelDepartmentEdit = () => {
        setEditingDepartmentId(null);
    };

    const saveDepartmentEdit = async () => {
        if (!validId || editingDepartmentId === null) return;
        setDepartmentsSaving(true);
        const updated = await updateClientDepartment(id, editingDepartmentId, {
            departmentName: editDepartmentName.trim(),
            description: editDepartmentDescription.trim() || null,
        });
        setDepartmentsSaving(false);
        if (updated) {
            setEditingDepartmentId(null);
            loadDepartments();
        }
    };

    const startAddUser = () => {
        setAddingUser(true);
        setNewUserUsername('');
        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserRoleIds([]);
    };

    const cancelAddUser = () => {
        setAddingUser(false);
        setNewUserUsername('');
        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserRoleIds([]);
        setShowNewUserPassword(false);
    };

    const saveNewUser = async () => {
        if (
            !validId ||
            !newUserUsername.trim() ||
            !newUserEmail.trim() ||
            !newUserPassword.trim() ||
            !validatePasswordPolicy(newUserPassword).valid
        )
            return;
        setUsersCreateSaving(true);
        const created = await createClientUser(id, {
            username: newUserUsername.trim(),
            email: newUserEmail.trim(),
            password: newUserPassword,
            roleIds: newUserRoleIds,
        });
        setUsersCreateSaving(false);
        if (created) {
            cancelAddUser();
            loadUsers();
        }
    };

    const startUserEdit = (user: ClientUserItem) => {
        setEditingUserId(user.userId);
        setEditUserRoleIds([...user.roleIds]);
        setEditUserActive(user.isActive);
        setEditUserNewPassword('');
        setShowEditUserNewPassword(false);
    };

    const cancelUserEdit = () => {
        setEditingUserId(null);
        setEditUserNewPassword('');
        setShowEditUserNewPassword(false);
    };

    const saveUserEdit = async () => {
        if (!validId || editingUserId === null) return;
        if (
            editUserNewPassword.trim() &&
            !validatePasswordPolicy(editUserNewPassword).valid
        )
            return;
        setUsersSaving(true);
        const updated = await updateClientUser(id, editingUserId, {
            roleIds: editUserRoleIds,
            isActive: editUserActive,
            newPassword: editUserNewPassword.trim() || undefined,
        });
        setUsersSaving(false);
        if (updated) {
            setEditingUserId(null);
            setEditUserNewPassword('');
            loadUsers();
        }
    };

    const handleActiveToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!client || togglingActive) return;
        const checked = event.target.checked;
        setTogglingActive(true);
        const updated = await updateClient(client.clientId, { isActive: checked });
        if (updated) {
            setClient(updated);
        }
        setTogglingActive(false);
    };

    if (!validId) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography sx={{ color: theme.palette.text.secondary }}>Invalid client ID.</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 1, textTransform: 'none' }}>
                    Back to clients
                </Button>
            </Box>
        );
    }

    if (error && !client) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography sx={{ color: theme.palette.error.main }}>{error}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 1, textTransform: 'none' }}>
                    Back to clients
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ mb: 2, color: theme.palette.text.secondary, textTransform: 'none' }}
            >
                Back to clients
            </Button>

            {loading && !client ? (
                <Box sx={{ py: 2 }}>
                    <Skeleton variant="text" width={280} height={40} />
                    <Skeleton variant="text" width={180} height={28} sx={{ mt: 1 }} />
                </Box>
            ) : (
                <>
                    <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 1 }}>
                        {client?.clientName ?? 'Client'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                        Subdomain: {client?.subdomain ?? '—'}
                    </Typography>
                </>
            )}

            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                    borderBottom: 1,
                    borderColor: theme.palette.divider,
                    mb: 2,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
                }}
            >
                <Tab
                    id="client-tab-0"
                    aria-controls="client-tabpanel-0"
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            Overview
                            {hasUnsavedOverview && (
                                <Typography
                                    component="span"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        opacity: 0.8,
                                        fontSize: '1.2rem',
                                        lineHeight: 1,
                                        ml: 0.5,
                                        fontWeight: 500,
                                    }}
                                >
                                    •
                                </Typography>
                            )}
                        </Box>
                    }
                />
                <Tab
                    id="client-tab-1"
                    aria-controls="client-tabpanel-1"
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            Users
                            {hasUnsavedUsers && (
                                <Typography
                                    component="span"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        opacity: 0.8,
                                        fontSize: '1.2rem',
                                        lineHeight: 1,
                                        ml: 0.5,
                                        fontWeight: 500,
                                    }}
                                >
                                    •
                                </Typography>
                            )}
                        </Box>
                    }
                />
                <Tab
                    id="client-tab-2"
                    aria-controls="client-tabpanel-2"
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            Departments
                            {hasUnsavedDepartments && (
                                <Typography
                                    component="span"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        opacity: 0.8,
                                        fontSize: '1.2rem',
                                        lineHeight: 1,
                                        ml: 0.5,
                                        fontWeight: 500,
                                    }}
                                >
                                    •
                                </Typography>
                            )}
                        </Box>
                    }
                />
                <Tab
                    id="client-tab-3"
                    aria-controls="client-tabpanel-3"
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            Roles
                            {hasUnsavedRoles && (
                                <Typography
                                    component="span"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        opacity: 0.8,
                                        fontSize: '1.2rem',
                                        lineHeight: 1,
                                        ml: 0.5,
                                        fontWeight: 500,
                                    }}
                                >
                                    •
                                </Typography>
                            )}
                        </Box>
                    }
                />
                <Tab
                    id="client-tab-4"
                    aria-controls="client-tabpanel-4"
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            Settings
                            {hasUnsavedSettings && (
                                <Typography component="span" sx={{ color: theme.palette.primary.main, opacity: 0.8, fontSize: '1.2rem', lineHeight: 1, ml: 0.5, fontWeight: 500 }}>
                                    •
                                </Typography>
                            )}
                        </Box>
                    }
                />
                <Tab label="Badge" id="client-tab-5" aria-controls="client-tabpanel-5" />
                <Tab label="Reports" id="client-tab-6" aria-controls="client-tabpanel-6" />
            </Tabs>

            {activeTab === TAB_OVERVIEW && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                    {loading && !client ? (
                        <Skeleton variant="rectangular" height={120} />
                    ) : overviewEditMode && overviewForm ? (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={saveOverviewEdit}
                                    disabled={!overviewDirty || overviewSaving}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {overviewSaving ? 'Saving…' : 'Save'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={cancelOverviewEdit}
                                    disabled={overviewSaving}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                                {overviewDirty && (
                                    <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
                                        Unsaved changes
                                    </Typography>
                                )}
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Name"
                                        value={overviewForm.clientName}
                                        onChange={(e) => setOverviewForm((f) => (f ? { ...f, clientName: e.target.value } : f))}
                                        disabled={overviewSaving}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.default } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Subdomain
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.subdomain ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Contact email"
                                        type="email"
                                        value={overviewForm.contactEmail}
                                        onChange={(e) => setOverviewForm((f) => (f ? { ...f, contactEmail: e.target.value } : f))}
                                        disabled={overviewSaving}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.default } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Contact phone"
                                        value={overviewForm.contactPhone}
                                        onChange={(e) => setOverviewForm((f) => (f ? { ...f, contactPhone: e.target.value } : f))}
                                        disabled={overviewSaving}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.default } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Address"
                                        multiline
                                        minRows={2}
                                        value={overviewForm.clientAddress}
                                        onChange={(e) => setOverviewForm((f) => (f ? { ...f, clientAddress: e.target.value } : f))}
                                        disabled={overviewSaving}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.default } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Industry"
                                        value={overviewForm.clientIndustry}
                                        onChange={(e) => setOverviewForm((f) => (f ? { ...f, clientIndustry: e.target.value } : f))}
                                        disabled={overviewSaving}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.default } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Package ID"
                                        value={overviewForm.packageId}
                                        onChange={(e) => setOverviewForm((f) => (f ? { ...f, packageId: e.target.value.replace(/\D/g, '') } : f))}
                                        disabled={overviewSaving}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.default } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={overviewForm.isActive}
                                                onChange={(e) => setOverviewForm((f) => (f ? { ...f, isActive: e.target.checked } : f))}
                                                disabled={overviewSaving}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                                {overviewForm.isActive ? 'Active' : 'Paused'}
                                            </Typography>
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Sign up date
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.signUpDate ? formatFriendlyDate(client.signUpDate) : '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Commissioning date
                                        </Typography>
                                        <Tooltip title={COMMISSIONING_DATE_TOOLTIP} placement="top" arrow>
                                            <IconButton size="small" sx={{ color: theme.palette.text.secondary, p: 0.25 }} aria-label="What is commissioning date?">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        value={overviewForm.commissioningDate}
                                        onChange={(e) => setOverviewForm((f) => (f ? { ...f, commissioningDate: e.target.value } : f))}
                                        disabled={overviewSaving}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.default } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Record created
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.recordCreateDate ? formatFriendlyDate(client.recordCreateDate) : '—'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={startOverviewEdit}
                                    disabled={!client}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Edit
                                </Button>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={client?.isActive ?? false}
                                            onChange={handleActiveToggle}
                                            disabled={!client || togglingActive}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                            {client?.isActive ? 'Active' : 'Paused'}
                                        </Typography>
                                    }
                                />
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Name
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.clientName ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Subdomain
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.subdomain ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Contact email
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.contactEmail ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Contact phone
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.contactPhone ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Address
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.clientAddress ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Industry
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.clientIndustry ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Package ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.packageId != null ? String(client.packageId) : '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Sign up date
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.signUpDate ? formatFriendlyDate(client.signUpDate) : '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Commissioning date
                                        </Typography>
                                        <Tooltip title={COMMISSIONING_DATE_TOOLTIP} placement="top" arrow>
                                            <IconButton size="small" sx={{ color: theme.palette.text.secondary, p: 0.25 }} aria-label="What is commissioning date?">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.commissioningDate ? formatFriendlyDate(client.commissioningDate) : '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Record created
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                                        {client?.recordCreateDate ? formatFriendlyDate(client.recordCreateDate) : '—'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Paper>
            )}

            {activeTab === TAB_USERS && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                    {usersLoading && usersData === null ? (
                        <Skeleton variant="rectangular" height={120} />
                    ) : usersData === null ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                            <Typography sx={{ color: theme.palette.text.secondary }}>
                                Users could not be loaded. Check your connection and permissions, then try again.
                            </Typography>
                            <Button variant="contained" size="small" onClick={() => loadUsers()} sx={{ textTransform: 'none' }}>
                                Retry
                            </Button>
                        </Box>
                    ) : (
                        <>
                            {!addingUser && editingUserId === null && (
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={startAddUser}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Add user
                                    </Button>
                                </Box>
                            )}
                            {addingUser && (
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        bgcolor: theme.palette.background.default,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                        New user
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Username"
                                                required
                                                value={newUserUsername}
                                                onChange={(e) => setNewUserUsername(e.target.value)}
                                                disabled={usersCreateSaving}
                                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Email"
                                                type="email"
                                                required
                                                value={newUserEmail}
                                                onChange={(e) => setNewUserEmail(e.target.value)}
                                                disabled={usersCreateSaving}
                                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Password"
                                                    type={showNewUserPassword ? 'text' : 'password'}
                                                    required
                                                    value={newUserPassword}
                                                    onChange={(e) => setNewUserPassword(e.target.value)}
                                                    disabled={usersCreateSaving}
                                                    helperText={
                                                        newUserPassword
                                                            ? validatePasswordPolicy(newUserPassword).valid
                                                                ? undefined
                                                                : validatePasswordPolicy(newUserPassword).message
                                                            : PASSWORD_POLICY_DESCRIPTION
                                                    }
                                                    error={
                                                        newUserPassword.length > 0 &&
                                                        !validatePasswordPolicy(newUserPassword).valid
                                                    }
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end" sx={{ mr: 0.5 }}>
                                                                <Tooltip title={showNewUserPassword ? 'Hide password' : 'Show password'}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => setShowNewUserPassword((v) => !v)}
                                                                        disabled={usersCreateSaving}
                                                                        aria-label={showNewUserPassword ? 'Hide password' : 'Show password'}
                                                                    >
                                                                        {showNewUserPassword ? (
                                                                            <VisibilityOffIcon fontSize="small" />
                                                                        ) : (
                                                                            <VisibilityIcon fontSize="small" />
                                                                        )}
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Copy password">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            if (newUserPassword) {
                                                                                navigator.clipboard.writeText(newUserPassword);
                                                                                setCopySnackbarOpen(true);
                                                                            }
                                                                        }}
                                                                        disabled={usersCreateSaving || !newUserPassword}
                                                                        aria-label="Copy password"
                                                                    >
                                                                        <ContentCopyIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                                />
                                                <Tooltip title="Generate a random strong password that meets the policy and fill the field">
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            startIcon={<RefreshIcon fontSize="small" />}
                                                            onClick={() => setNewUserPassword(generateStrongPassword())}
                                                            disabled={usersCreateSaving}
                                                            sx={{ mt: 0.5, textTransform: 'none' }}
                                                            aria-label="Generate strong password"
                                                        >
                                                            Generate
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </Grid>
                                        {rolesData && rolesData.roles.length > 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                                                    Roles
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    {rolesData.roles.map((role) => (
                                                        <Grid item xs={12} sm={6} md={4} key={role.roleId}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={newUserRoleIds.includes(role.roleId)}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setNewUserRoleIds((prev) => [...prev, role.roleId]);
                                                                            } else {
                                                                                setNewUserRoleIds((prev) => prev.filter((id) => id !== role.roleId));
                                                                            }
                                                                        }}
                                                                        disabled={usersCreateSaving}
                                                                        size="small"
                                                                    />
                                                                }
                                                                label={<Typography variant="body2">{role.roleName}</Typography>}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={saveNewUser}
                                                    disabled={
                                                        !newUserUsername.trim() ||
                                                        !newUserEmail.trim() ||
                                                        !newUserPassword ||
                                                        !validatePasswordPolicy(newUserPassword).valid ||
                                                        usersCreateSaving
                                                    }
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {usersCreateSaving ? 'Creating…' : 'Create'}
                                                </Button>
                                                <Button variant="outlined" size="small" onClick={cancelAddUser} disabled={usersCreateSaving} sx={{ textTransform: 'none' }}>
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {usersData.length === 0 && !addingUser ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                        <Typography variant="body1" color="text.secondary">
                                                            No users yet
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
                                                            Add users to grant access to this client. Assign roles to control permissions.
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<AddIcon />}
                                                            onClick={startAddUser}
                                                            sx={{ textTransform: 'none', mt: 1 }}
                                                        >
                                                            Add user
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            usersData.map((user) => (
                                                <React.Fragment key={user.userId}>
                                                    <TableRow>
                                                        {editingUserId === user.userId ? (
                                                            <>
                                                                <TableCell sx={{ color: theme.palette.text.primary }}>{user.username}</TableCell>
                                                                <TableCell sx={{ color: theme.palette.text.secondary }}>{user.email}</TableCell>
                                                                <TableCell colSpan={1} sx={{ py: 1 }}>
                                                                    {rolesData && rolesData.roles.length > 0 ? (
                                                                        <Grid container spacing={0.5}>
                                                                            {rolesData.roles.map((role) => (
                                                                                <Grid item xs={12} sm={6} key={role.roleId}>
                                                                                    <FormControlLabel
                                                                                        control={
                                                                                            <Checkbox
                                                                                                checked={editUserRoleIds.includes(role.roleId)}
                                                                                                onChange={(e) => {
                                                                                                    if (e.target.checked) {
                                                                                                        setEditUserRoleIds((prev) => [...prev, role.roleId]);
                                                                                                    } else {
                                                                                                        setEditUserRoleIds((prev) => prev.filter((id) => id !== role.roleId));
                                                                                                    }
                                                                                                }}
                                                                                                disabled={usersSaving}
                                                                                                size="small"
                                                                                            />
                                                                                        }
                                                                                        label={<Typography variant="body2">{role.roleName}</Typography>}
                                                                                    />
                                                                                </Grid>
                                                                            ))}
                                                                        </Grid>
                                                                    ) : (
                                                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ py: 1 }}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={editUserActive}
                                                                                onChange={(e) => setEditUserActive(e.target.checked)}
                                                                                disabled={usersSaving}
                                                                                size="small"
                                                                            />
                                                                        }
                                                                        label={<Typography variant="body2">{editUserActive ? 'Active' : 'Inactive'}</Typography>}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ py: 1 }}>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                                            <TextField
                                                                                size="small"
                                                                                type={showEditUserNewPassword ? 'text' : 'password'}
                                                                                placeholder="New password (optional)"
                                                                                value={editUserNewPassword}
                                                                                onChange={(e) => setEditUserNewPassword(e.target.value)}
                                                                                disabled={usersSaving}
                                                                                helperText={
                                                                                    editUserNewPassword &&
                                                                                    !validatePasswordPolicy(editUserNewPassword).valid
                                                                                        ? validatePasswordPolicy(editUserNewPassword).message
                                                                                        : undefined
                                                                                }
                                                                                error={
                                                                                    editUserNewPassword.length > 0 &&
                                                                                    !validatePasswordPolicy(editUserNewPassword).valid
                                                                                }
                                                                                InputProps={{
                                                                                    endAdornment: (
                                                                                        <InputAdornment position="end" sx={{ mr: 0.25 }}>
                                                                                            <Tooltip title={showEditUserNewPassword ? 'Hide password' : 'Show password'}>
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() => setShowEditUserNewPassword((v) => !v)}
                                                                                                    disabled={usersSaving}
                                                                                                    aria-label={showEditUserNewPassword ? 'Hide password' : 'Show password'}
                                                                                                >
                                                                                                    {showEditUserNewPassword ? (
                                                                                                        <VisibilityOffIcon fontSize="small" />
                                                                                                    ) : (
                                                                                                        <VisibilityIcon fontSize="small" />
                                                                                                    )}
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                            <Tooltip title="Copy password">
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() => {
                                                                                                        if (editUserNewPassword) {
                                                                                                            navigator.clipboard.writeText(editUserNewPassword);
                                                                                                            setCopySnackbarOpen(true);
                                                                                                        }
                                                                                                    }}
                                                                                                    disabled={usersSaving || !editUserNewPassword}
                                                                                                    aria-label="Copy password"
                                                                                                >
                                                                                                    <ContentCopyIcon fontSize="small" />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                        </InputAdornment>
                                                                                    ),
                                                                                }}
                                                                                sx={{ width: 180, '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                                                            />
                                                                            <Tooltip title="Generate a random strong password that meets the policy and fill the field">
                                                                                <span>
                                                                                    <Button
                                                                                        size="small"
                                                                                        startIcon={<RefreshIcon fontSize="small" />}
                                                                                        onClick={() => setEditUserNewPassword(generateStrongPassword())}
                                                                                        disabled={usersSaving}
                                                                                        sx={{ mt: 0.5, textTransform: 'none' }}
                                                                                        aria-label="Generate strong password"
                                                                                    >
                                                                                        Generate
                                                                                    </Button>
                                                                                </span>
                                                                            </Tooltip>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                                            <Button
                                                                                variant="contained"
                                                                                size="small"
                                                                                onClick={saveUserEdit}
                                                                                disabled={
                                                                                    !userEditDirty ||
                                                                                    usersSaving ||
                                                                                    (editUserNewPassword.length > 0 &&
                                                                                        !validatePasswordPolicy(editUserNewPassword).valid)
                                                                                }
                                                                                sx={{ textTransform: 'none' }}
                                                                            >
                                                                                {usersSaving ? 'Saving…' : 'Save'}
                                                                            </Button>
                                                                            <Button variant="outlined" size="small" onClick={cancelUserEdit} disabled={usersSaving} sx={{ textTransform: 'none' }}>
                                                                                Cancel
                                                                            </Button>
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TableCell sx={{ color: theme.palette.text.primary }}>{user.username}</TableCell>
                                                                <TableCell sx={{ color: theme.palette.text.secondary }}>{user.email}</TableCell>
                                                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                                                    {rolesData
                                                                        ? user.roleIds
                                                                                .map((rid) => rolesData.roles.find((r) => r.roleId === rid)?.roleName ?? rid)
                                                                                .filter(Boolean)
                                                                                .join(', ') || '—'
                                                                        : `${user.roleIds.length} role(s)`}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" color={user.isActive ? 'text.primary' : 'text.secondary'}>
                                                                        {user.isActive ? 'Active' : 'Inactive'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        startIcon={<EditIcon />}
                                                                        onClick={() => startUserEdit(user)}
                                                                        disabled={addingUser}
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                </TableCell>
                                                            </>
                                                        )}
                                                    </TableRow>
                                                </React.Fragment>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Paper>
            )}

            {activeTab === TAB_DEPARTMENTS && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                    {departmentsLoading && departmentsData === null ? (
                        <Skeleton variant="rectangular" height={120} />
                    ) : departmentsData === null ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                            <Typography sx={{ color: theme.palette.text.secondary }}>
                                Departments could not be loaded. Check your connection and permissions, then try again.
                            </Typography>
                            <Button variant="contained" size="small" onClick={() => loadDepartments()} sx={{ textTransform: 'none' }}>
                                Retry
                            </Button>
                        </Box>
                    ) : (
                        <>
                            {!addingDepartment && editingDepartmentId === null && (
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={startAddDepartment}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Add department
                                    </Button>
                                </Box>
                            )}
                            {addingDepartment && (
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        bgcolor: theme.palette.background.default,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                        New department
                                    </Typography>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Department name"
                                                required
                                                value={newDepartmentName}
                                                onChange={(e) => setNewDepartmentName(e.target.value)}
                                                disabled={departmentsCreateSaving}
                                                placeholder="e.g. Sales"
                                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Description (optional)"
                                                value={newDepartmentDescription}
                                                onChange={(e) => setNewDepartmentDescription(e.target.value)}
                                                disabled={departmentsCreateSaving}
                                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={saveNewDepartment}
                                                    disabled={!newDepartmentName.trim() || departmentsCreateSaving}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {departmentsCreateSaving ? 'Creating…' : 'Create'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={cancelAddDepartment}
                                                    disabled={departmentsCreateSaving}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {departmentsData.length === 0 && !addingDepartment ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                        <Typography variant="body1" color="text.secondary">
                                                            No departments yet
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
                                                            Add departments to organize users and collection points for this client.
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<AddIcon />}
                                                            onClick={startAddDepartment}
                                                            sx={{ textTransform: 'none', mt: 1 }}
                                                        >
                                                            Add department
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            departmentsData.map((dept) => (
                                                <React.Fragment key={dept.departmentId}>
                                                    <TableRow>
                                                        {editingDepartmentId === dept.departmentId ? (
                                                            <>
                                                                <TableCell sx={{ color: theme.palette.text.primary }}>
                                                                    <TextField
                                                                        size="small"
                                                                        value={editDepartmentName}
                                                                        onChange={(e) => setEditDepartmentName(e.target.value)}
                                                                        disabled={departmentsSaving}
                                                                        placeholder="Department name"
                                                                        sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        value={editDepartmentDescription}
                                                                        onChange={(e) => setEditDepartmentDescription(e.target.value)}
                                                                        disabled={departmentsSaving}
                                                                        placeholder="Description (optional)"
                                                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            onClick={saveDepartmentEdit}
                                                                            disabled={!editDepartmentName.trim() || !departmentEditDirty || departmentsSaving}
                                                                            sx={{ textTransform: 'none' }}
                                                                        >
                                                                            {departmentsSaving ? 'Saving…' : 'Save'}
                                                                        </Button>
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            onClick={cancelDepartmentEdit}
                                                                            disabled={departmentsSaving}
                                                                            sx={{ textTransform: 'none' }}
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                    </Box>
                                                                </TableCell>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TableCell sx={{ color: theme.palette.text.primary }}>{dept.departmentName}</TableCell>
                                                                <TableCell sx={{ color: theme.palette.text.secondary }}>{dept.description ?? '—'}</TableCell>
                                                                <TableCell align="right">
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        startIcon={<EditIcon />}
                                                                        onClick={() => startDepartmentEdit(dept)}
                                                                        disabled={addingDepartment}
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                </TableCell>
                                                            </>
                                                        )}
                                                    </TableRow>
                                                </React.Fragment>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Paper>
            )}

            {activeTab === TAB_ROLES && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                    {rolesLoading && !rolesData ? (
                        <Skeleton variant="rectangular" height={120} />
                    ) : !rolesData ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                            <Typography sx={{ color: theme.palette.text.secondary }}>
                                Roles could not be loaded. Check your connection and permissions, then try again.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button variant="contained" size="small" onClick={() => loadRoles()} sx={{ textTransform: 'none' }}>
                                    Retry
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        openAddFormWhenRolesLoadedRef.current = true;
                                        loadRoles();
                                    }}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Add role
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {rolesData.isSuperAdminClient === true && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        mb: 1,
                                        color: theme.palette.text.secondary,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    This client can assign super-admin permissions (Manage client, Manage packages).
                                </Typography>
                            )}
                            {!addingRole && editingRoleId === null && (
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={startAddRole}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Add role
                                    </Button>
                                </Box>
                            )}
                            {addingRole && (
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        bgcolor: theme.palette.background.default,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                        New role
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Role name"
                                                required
                                                value={newRoleName}
                                                onChange={(e) => setNewRoleName(e.target.value)}
                                                disabled={rolesCreateSaving}
                                                placeholder="e.g. Manager"
                                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Description (optional)"
                                                value={newRoleDescription}
                                                onChange={(e) => setNewRoleDescription(e.target.value)}
                                                disabled={rolesCreateSaving}
                                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                                                Permissions
                                            </Typography>
                                            <Grid container spacing={1}>
                                                {rolesData.allPermissions.map((perm) => (
                                                    <Grid item xs={12} sm={6} md={4} key={perm.permissionId}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={newRolePermissionIds.includes(perm.permissionId)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setNewRolePermissionIds((prev) => [...prev, perm.permissionId]);
                                                                        } else {
                                                                            setNewRolePermissionIds((prev) =>
                                                                                prev.filter((id) => id !== perm.permissionId)
                                                                            );
                                                                        }
                                                                    }}
                                                                    disabled={rolesCreateSaving}
                                                                    size="small"
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                                                    {perm.permissionName}
                                                                </Typography>
                                                            }
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={saveNewRole}
                                                    disabled={!newRoleName.trim() || rolesCreateSaving}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {rolesCreateSaving ? 'Creating…' : 'Create role'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={cancelAddRole}
                                                    disabled={rolesCreateSaving}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rolesData.roles.length === 0 && !addingRole ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                        <Typography variant="body1" color="text.secondary">
                                                            No roles yet
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
                                                            Create roles and assign permissions to control what users can do for this client.
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<AddIcon />}
                                                            onClick={startAddRole}
                                                            sx={{ textTransform: 'none', mt: 1 }}
                                                        >
                                                            Add role
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            rolesData.roles.map((role) => (
                                            <React.Fragment key={role.roleId}>
                                                <TableRow>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                                        {role.roleName}
                                                        {role.isSystemRole && (
                                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                                                                (system)
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                                                        {role.description ?? '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                                        {role.permissionIds.length} permission{role.permissionIds.length !== 1 ? 's' : ''}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {editingRoleId === role.roleId ? (
                                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    onClick={saveRolePermissions}
                                                                    disabled={!rolesDirty || rolesSaving}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    {rolesSaving ? 'Saving…' : 'Save'}
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={cancelRoleEdit}
                                                                    disabled={rolesSaving}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<EditIcon />}
                                                                onClick={() => startRoleEdit(role)}
                                                                disabled={addingRole}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                {editingRoleId === role.roleId && (
                                                    <TableRow>
                                                        <TableCell colSpan={4} sx={{ py: 2, bgcolor: theme.palette.background.default }}>
                                                            <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                                                Permissions
                                                            </Typography>
                                                            <Grid container spacing={1}>
                                                                {rolesData.allPermissions.map((perm) => (
                                                                    <Grid item xs={12} sm={6} md={4} key={perm.permissionId}>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={editedPermissionIds.includes(perm.permissionId)}
                                                                                    onChange={(e) => {
                                                                                        if (e.target.checked) {
                                                                                            setEditedPermissionIds((prev) => [...prev, perm.permissionId]);
                                                                                        } else {
                                                                                            setEditedPermissionIds((prev) =>
                                                                                                prev.filter((id) => id !== perm.permissionId)
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                    disabled={rolesSaving}
                                                                                    size="small"
                                                                                />
                                                                            }
                                                                            label={
                                                                                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                                                                    {perm.permissionName}
                                                                                </Typography>
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Paper>
            )}

            {activeTab === TAB_SETTINGS && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                    {settingsLoading && settingsData === null ? (
                        <Skeleton variant="rectangular" height={120} />
                    ) : (
                        <>
                            {/* 14a. ClientSettings – Notification & SLA */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                                Notification & SLA
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Withdrawal SLA (days)"
                                        type="number"
                                        value={settingsForm.WITHDRAWAL_SLA_DAYS ?? ''}
                                        onChange={(e) =>
                                            setSettingsForm((prev) => ({ ...prev, WITHDRAWAL_SLA_DAYS: e.target.value }))
                                        }
                                        disabled={settingsSaving}
                                        inputProps={{ min: 1, max: 365 }}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.default } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={(settingsForm.PHONE_NOTIFICATION_ENABLE_SMS ?? 'false') === 'true'}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        PHONE_NOTIFICATION_ENABLE_SMS: e.target.checked ? 'true' : 'false',
                                                    }))
                                                }
                                                disabled={settingsSaving}
                                                color="primary"
                                            />
                                        }
                                        label="Enable SMS"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={(settingsForm.PHONE_NOTIFICATION_ENABLE_WHATSAPP ?? 'false') === 'true'}
                                                onChange={(e) =>
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        PHONE_NOTIFICATION_ENABLE_WHATSAPP: e.target.checked ? 'true' : 'false',
                                                    }))
                                                }
                                                disabled={settingsSaving}
                                                color="primary"
                                            />
                                        }
                                        label="Enable WhatsApp"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small" sx={{ minWidth: 180 }}>
                                        <InputLabel>Preferred method</InputLabel>
                                        <Select
                                            value={settingsForm.PHONE_NOTIFICATION_PREFERRED_METHOD ?? 'SMS'}
                                            label="Preferred method"
                                            onChange={(e) =>
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    PHONE_NOTIFICATION_PREFERRED_METHOD: e.target.value,
                                                }))
                                            }
                                            disabled={settingsSaving}
                                            sx={{ bgcolor: theme.palette.background.default }}
                                        >
                                            <MenuItem value="SMS">SMS</MenuItem>
                                            <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={async () => {
                                            if (!validId) return;
                                            setSettingsSaving(true);
                                            const updated = await patchClientSettings(id, {
                                                WITHDRAWAL_SLA_DAYS: settingsForm.WITHDRAWAL_SLA_DAYS ?? '7',
                                                PHONE_NOTIFICATION_ENABLE_SMS: settingsForm.PHONE_NOTIFICATION_ENABLE_SMS ?? 'false',
                                                PHONE_NOTIFICATION_ENABLE_WHATSAPP: settingsForm.PHONE_NOTIFICATION_ENABLE_WHATSAPP ?? 'false',
                                                PHONE_NOTIFICATION_PREFERRED_METHOD: settingsForm.PHONE_NOTIFICATION_PREFERRED_METHOD ?? 'SMS',
                                            });
                                            setSettingsSaving(false);
                                            if (updated) {
                                                setSettingsData(updated);
                                                setSettingsForm(updated);
                                            }
                                        }}
                                        disabled={!settingsFormDirty || settingsSaving}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        {settingsSaving ? 'Saving…' : 'Save'}
                                    </Button>
                                </Grid>
                            </Grid>

                            {/* 14b. SMS provider – single form always visible */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3, color: theme.palette.text.primary }}>
                                SMS provider
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                                {smsConfigLoading ? (
                                    <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
                                ) : (
                                    <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: theme.palette.background.default, mb: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="Provider" value={smsForm.providerName} onChange={(e) => setSmsForm((p) => ({ ...p, providerName: e.target.value }))} disabled={smsSaving} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="API Key" type="password" value={smsForm.apiKey} onChange={(e) => setSmsForm((p) => ({ ...p, apiKey: e.target.value }))} disabled={smsSaving} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="Template ID" value={smsForm.templateId ?? ''} onChange={(e) => setSmsForm((p) => ({ ...p, templateId: e.target.value }))} disabled={smsSaving} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="OTP Template ID" value={smsForm.otpTemplateId ?? ''} onChange={(e) => setSmsForm((p) => ({ ...p, otpTemplateId: e.target.value }))} disabled={smsSaving} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControlLabel control={<Switch checked={smsForm.shortUrlEnabled ?? false} onChange={(e) => setSmsForm((p) => ({ ...p, shortUrlEnabled: e.target.checked }))} disabled={smsSaving} />} label="Short URL enabled" />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="Short URL expiry (days)" type="number" value={smsForm.shortUrlExpiryDays ?? ''} onChange={(e) => setSmsForm((p) => ({ ...p, shortUrlExpiryDays: e.target.value ? parseInt(e.target.value, 10) : null }))} disabled={smsSaving} />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button variant="contained" size="small" sx={{ textTransform: 'none' }} disabled={smsSaving || !smsForm.providerName || !smsForm.apiKey || !smsFormDirty} onClick={async () => { if (!validId) return; setSmsSaving(true); const saved = await putClientSmsConfig(id, smsForm); setSmsSaving(false); if (saved) { setSmsConfig(saved); setSmsForm({ ...saved }); } }}>
                                                    {smsSaving ? 'Saving…' : 'Save'}
                                                </Button>
                                                {smsFormDirty && (
                                                    <Button variant="outlined" size="small" sx={{ textTransform: 'none', ml: 1 }} disabled={smsSaving} onClick={() => setSmsForm(smsConfig ? { ...smsConfig } : emptySmsForm())}>Cancel</Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                        <InputLabel>Copy from client</InputLabel>
                                        <Select
                                            value={copySourceClientIdSms}
                                            label="Copy from client"
                                            onChange={(e) => setCopySourceClientIdSms(e.target.value)}
                                            sx={{ bgcolor: theme.palette.background.paper }}
                                        >
                                            <MenuItem value="">Select client</MenuItem>
                                            {clientsList.filter((c) => c.clientId !== id).map((c) => (
                                                <MenuItem key={c.clientId} value={String(c.clientId)}>{c.clientName} ({c.clientId})</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                {copySourceClientIdSms && (
                                    <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: theme.palette.action.hover }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, color: theme.palette.text.primary }}>
                                            Preview: {clientsList.find((c) => c.clientId === parseInt(copySourceClientIdSms, 10))?.clientName ?? copySourceClientIdSms}&apos;s SMS config
                                            {copySourceSmsPreviewLoading && ' (loading…)'}
                                        </Typography>
                                        {copySourceSmsPreviewLoading ? (
                                            <Skeleton variant="rectangular" height={80} />
                                        ) : copySourceSmsPreview ? (
                                            <>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="Provider" value={copySourceSmsPreview.providerName} onChange={(e) => setCopySourceSmsPreview((p) => (p ? { ...p, providerName: e.target.value } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="API Key" type="password" value={copySourceSmsPreview.apiKey} onChange={(e) => setCopySourceSmsPreview((p) => (p ? { ...p, apiKey: e.target.value } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="Template ID" value={copySourceSmsPreview.templateId ?? ''} onChange={(e) => setCopySourceSmsPreview((p) => (p ? { ...p, templateId: e.target.value } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="OTP Template ID" value={copySourceSmsPreview.otpTemplateId ?? ''} onChange={(e) => setCopySourceSmsPreview((p) => (p ? { ...p, otpTemplateId: e.target.value } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControlLabel control={<Switch checked={copySourceSmsPreview.shortUrlEnabled ?? false} onChange={(e) => setCopySourceSmsPreview((p) => (p ? { ...p, shortUrlEnabled: e.target.checked } : p))} />} label="Short URL enabled" />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="Short URL expiry (days)" type="number" value={copySourceSmsPreview.shortUrlExpiryDays ?? ''} onChange={(e) => setCopySourceSmsPreview((p) => (p ? { ...p, shortUrlExpiryDays: e.target.value ? parseInt(e.target.value, 10) : null } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Button variant="contained" size="small" sx={{ textTransform: 'none' }} disabled={copySmsLoading || !copySourceSmsPreview.providerName || !copySourceSmsPreview.apiKey} onClick={async () => { if (!validId || !copySourceSmsPreview) return; setCopySmsLoading(true); const saved = await putClientSmsConfig(id, copySourceSmsPreview); setCopySmsLoading(false); if (saved) { setSmsConfig(saved); setSmsForm({ ...saved }); setCopySourceClientIdSms(''); setCopySourceSmsPreview(null); } }}>
                                                            {copySmsLoading ? 'Applying…' : 'Apply to this client'}
                                                        </Button>
                                                        <Button variant="outlined" size="small" sx={{ textTransform: 'none', ml: 1 }} onClick={() => { setCopySourceClientIdSms(''); setCopySourceSmsPreview(null); }}>Clear</Button>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        ) : null}
                                    </Box>
                                )}
                            </Box>

                            {/* 14c. WhatsApp provider – single form always visible */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3, color: theme.palette.text.primary }}>
                                WhatsApp provider
                            </Typography>
                            <Box>
                                {whatsappConfigLoading ? (
                                    <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
                                ) : (
                                    <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: theme.palette.background.default, mb: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="Provider" value={whatsappForm.providerName} onChange={(e) => setWhatsappForm((p) => ({ ...p, providerName: e.target.value }))} disabled={whatsappSaving} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="API Key" type="password" value={whatsappForm.apiKey} onChange={(e) => setWhatsappForm((p) => ({ ...p, apiKey: e.target.value }))} disabled={whatsappSaving} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="Phone number ID" value={whatsappForm.phoneNumberId ?? ''} onChange={(e) => setWhatsappForm((p) => ({ ...p, phoneNumberId: e.target.value }))} disabled={whatsappSaving} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth size="small" label="Template ID" value={whatsappForm.templateId ?? ''} onChange={(e) => setWhatsappForm((p) => ({ ...p, templateId: e.target.value }))} disabled={whatsappSaving} />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button variant="contained" size="small" sx={{ textTransform: 'none' }} disabled={whatsappSaving || !whatsappForm.providerName || !whatsappForm.apiKey || !whatsappFormDirty} onClick={async () => { if (!validId) return; setWhatsappSaving(true); const saved = await putClientWhatsAppConfig(id, whatsappForm); setWhatsappSaving(false); if (saved) { setWhatsappConfig(saved); setWhatsappForm({ ...saved }); } }}>
                                                    {whatsappSaving ? 'Saving…' : 'Save'}
                                                </Button>
                                                {whatsappFormDirty && (
                                                    <Button variant="outlined" size="small" sx={{ textTransform: 'none', ml: 1 }} disabled={whatsappSaving} onClick={() => setWhatsappForm(whatsappConfig ? { ...whatsappConfig } : emptyWhatsappForm())}>Cancel</Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                        <InputLabel>Copy from client</InputLabel>
                                        <Select
                                            value={copySourceClientIdWhatsapp}
                                            label="Copy from client"
                                            onChange={(e) => setCopySourceClientIdWhatsapp(e.target.value)}
                                            sx={{ bgcolor: theme.palette.background.paper }}
                                        >
                                            <MenuItem value="">Select client</MenuItem>
                                            {clientsList.filter((c) => c.clientId !== id).map((c) => (
                                                <MenuItem key={c.clientId} value={String(c.clientId)}>{c.clientName} ({c.clientId})</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                {copySourceClientIdWhatsapp && (
                                    <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: theme.palette.action.hover }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, color: theme.palette.text.primary }}>
                                            Preview: {clientsList.find((c) => c.clientId === parseInt(copySourceClientIdWhatsapp, 10))?.clientName ?? copySourceClientIdWhatsapp}&apos;s WhatsApp config
                                            {copySourceWhatsappPreviewLoading && ' (loading…)'}
                                        </Typography>
                                        {copySourceWhatsappPreviewLoading ? (
                                            <Skeleton variant="rectangular" height={80} />
                                        ) : copySourceWhatsappPreview ? (
                                            <>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="Provider" value={copySourceWhatsappPreview.providerName} onChange={(e) => setCopySourceWhatsappPreview((p) => (p ? { ...p, providerName: e.target.value } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="API Key" type="password" value={copySourceWhatsappPreview.apiKey} onChange={(e) => setCopySourceWhatsappPreview((p) => (p ? { ...p, apiKey: e.target.value } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="Phone number ID" value={copySourceWhatsappPreview.phoneNumberId ?? ''} onChange={(e) => setCopySourceWhatsappPreview((p) => (p ? { ...p, phoneNumberId: e.target.value } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" label="Template ID" value={copySourceWhatsappPreview.templateId ?? ''} onChange={(e) => setCopySourceWhatsappPreview((p) => (p ? { ...p, templateId: e.target.value } : p))} sx={{ '& .MuiOutlinedInput-root': { bgcolor: theme.palette.background.paper } }} />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Button variant="contained" size="small" sx={{ textTransform: 'none' }} disabled={copyWhatsappLoading || !copySourceWhatsappPreview.providerName || !copySourceWhatsappPreview.apiKey} onClick={async () => { if (!validId || !copySourceWhatsappPreview) return; setCopyWhatsappLoading(true); const saved = await putClientWhatsAppConfig(id, copySourceWhatsappPreview); setCopyWhatsappLoading(false); if (saved) { setWhatsappConfig(saved); setWhatsappForm({ ...saved }); setCopySourceClientIdWhatsapp(''); setCopySourceWhatsappPreview(null); } }}>
                                                            {copyWhatsappLoading ? 'Applying…' : 'Apply to this client'}
                                                        </Button>
                                                        <Button variant="outlined" size="small" sx={{ textTransform: 'none', ml: 1 }} onClick={() => { setCopySourceClientIdWhatsapp(''); setCopySourceWhatsappPreview(null); }}>Clear</Button>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        ) : null}
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            {activeTab === TAB_BADGE && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Privigo DPDPA badge
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                        Issue a Ready certificate, then copy the embed HTML into the customer website footer. Click opens the public verify page.
                    </Typography>
                    {certificateLoading ? (
                        <Skeleton variant="rectangular" height={160} />
                    ) : (
                        <>
                            {certificateError && (
                                <Typography color="error" sx={{ mb: 2 }}>
                                    {certificateError}
                                </Typography>
                            )}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Organisation name"
                                        value={badgeOrgName}
                                        onChange={(e) => setBadgeOrgName(e.target.value)}
                                        disabled={certificateIssuing}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Valid until"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={badgeValidUntil}
                                        onChange={(e) => setBadgeValidUntil(e.target.value)}
                                        disabled={certificateIssuing}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="DP portal URL (optional)"
                                        value={badgeDpPortalUrl}
                                        onChange={(e) => setBadgeDpPortalUrl(e.target.value)}
                                        disabled={certificateIssuing}
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={certificateIssuing || !badgeOrgName.trim()}
                                    sx={{ textTransform: 'none' }}
                                    onClick={async () => {
                                        if (!validId) return;
                                        setCertificateIssuing(true);
                                        setCertificateError(null);
                                        const issued = await issueClientCertificate(id, {
                                            tier: 'ready',
                                            organisationName: badgeOrgName.trim(),
                                            validUntil: badgeValidUntil || undefined,
                                            dpPortalUrl: badgeDpPortalUrl.trim() || null,
                                        });
                                        setCertificateIssuing(false);
                                        if (!issued) {
                                            setCertificateError('Failed to issue certificate');
                                            return;
                                        }
                                        setCertificate(issued);
                                        setBadgeOrgName(issued.organisationName);
                                        setBadgeValidUntil(issued.validUntil);
                                        setBadgeDpPortalUrl(issued.dpPortalUrl ?? '');
                                    }}
                                >
                                    {certificateIssuing
                                        ? 'Issuing…'
                                        : certificate
                                          ? 'Renew Ready badge'
                                          : 'Issue Ready badge'}
                                </Button>
                                {certificate && certificate.status !== 'revoked' && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        disabled={certificateRevoking}
                                        sx={{ textTransform: 'none' }}
                                        onClick={async () => {
                                            if (!validId) return;
                                            setCertificateRevoking(true);
                                            setCertificateError(null);
                                            const result = await revokeClientCertificate(id);
                                            setCertificateRevoking(false);
                                            if (!result) {
                                                setCertificateError('Failed to revoke certificate');
                                                return;
                                            }
                                            setCertificate(null);
                                            loadCertificate();
                                        }}
                                    >
                                        {certificateRevoking ? 'Revoking…' : 'Revoke'}
                                    </Button>
                                )}
                            </Box>
                            {certificate ? (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Status:</strong> {certificate.status} · <strong>ID:</strong>{' '}
                                        {certificate.certId} · {certificate.tierLabel}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Issued {certificate.issuedAt} · Valid until {certificate.validUntil}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Verify URL:{' '}
                                        <a href={certificate.verifyUrl} target="_blank" rel="noopener noreferrer">
                                            {certificate.verifyUrl}
                                        </a>
                                    </Typography>
                                    <Box sx={{ my: 2 }}>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                                            Preview
                                        </Typography>
                                        <Box
                                            sx={{ display: 'inline-block' }}
                                            dangerouslySetInnerHTML={{ __html: certificate.embedHtml }}
                                        />
                                    </Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Embed HTML (paste into customer site)
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        size="small"
                                        value={certificate.embedHtml}
                                        InputProps={{ readOnly: true }}
                                        sx={{ mb: 1, fontFamily: 'monospace' }}
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ContentCopyIcon />}
                                        sx={{ textTransform: 'none' }}
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(certificate.embedHtml);
                                                setEmbedCopiedSnackbar(true);
                                            } catch {
                                                setCertificateError('Could not copy to clipboard');
                                            }
                                        }}
                                    >
                                        Copy embed HTML
                                    </Button>
                                </Box>
                            ) : (
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    No active certificate. Issue a Ready badge to generate embed HTML.
                                </Typography>
                            )}
                        </>
                    )}
                </Paper>
            )}

            {activeTab === TAB_REPORTS && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                    <Typography sx={{ color: theme.palette.text.secondary }}>Reports – coming soon</Typography>
                </Paper>
            )}

            <Dialog open={tabSwitchConfirmOpen} onClose={handleTabSwitchCancel}>
                <DialogTitle>Unsaved changes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {tabSwitchConfirmMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleTabSwitchCancel} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleTabSwitchConfirm} color="primary" variant="contained" sx={{ textTransform: 'none' }}>
                        Discard and switch
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={copySnackbarOpen}
                onClose={() => setCopySnackbarOpen(false)}
                autoHideDuration={2000}
                message="Copied to clipboard"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
            <Snackbar
                open={embedCopiedSnackbar}
                onClose={() => setEmbedCopiedSnackbar(false)}
                autoHideDuration={2000}
                message="Embed HTML copied"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default ClientDetailPage;
